import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  ExternalLink, Copy, Check, Users, BookOpen, MessageSquare,
  Award, Sparkles, Plus, Play, Globe, CheckCircle2,
  Download, Eye, ShieldCheck, Flame, Star, Bot, Code, HelpCircle,
  RefreshCw, Trash2, ArrowRight, Palette, QrCode, Calendar, Clock, Sliders, Type
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import AdvancedCertificateStudio from "./memberships/AdvancedCertificateStudio";

const INITIAL_COURSES = [
  {
    id: "c-llm",
    title: "Learning Large Language Models: From Architecture to Autonomous Agents",
    modules: 6,
    students: 84,
    progress: "60%",
    cover: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=600&auto=format&fit=crop&q=80",
    desc: "Master the fundamentals of modern LLMs, Transformer attention mechanics, token embeddings, LoRA fine-tuning, and multi-agent tool execution.",
    badge: "Flagship AI Course",
  },
  {
    id: "c1",
    title: "LaunchDeck Marketing Masterclass",
    modules: 6,
    students: 48,
    progress: "100%",
    cover: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&auto=format&fit=crop&q=80",
    desc: "Complete guide to automated email marketing, lead capture, and CRM pipeline closing.",
  },
  {
    id: "c2",
    title: "High-Converting Content & Media Strategy",
    modules: 4,
    students: 32,
    progress: "75%",
    cover: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=600&auto=format&fit=crop&q=80",
    desc: "How to leverage Colibri Studio visual assets for multi-platform viral campaigns.",
  },
];

// Clients, communities, and issued credentials start empty for new accounts
const INITIAL_CLIENTS = [];
const INITIAL_COMMUNITIES = [];
const INITIAL_CREDENTIALS = [];

const SNAPSHOTS = [
  { id: "snap-1", title: "Luxury Hospitality & Hotel Snapshot", author: "Colibri Agency", installs: 840, rating: 4.9, tags: ["Funnels", "Email Automation", "Pipelines"] },
  { id: "snap-2", title: "Local Service Contractor Engine", author: "Growth Labs", installs: 1250, rating: 5.0, tags: ["SMS Review Bot", "Booking Calendar", "Forms"] },
  { id: "snap-3", title: "Commercial Real Estate Retainer", author: "LaunchDeck Official", installs: 620, rating: 4.8, tags: ["Property Portfolios", "Client Portal", "Invoicing"] },
];

const LLM_SYLLABUS = [
  {
    module: "Module 1: Transformer & Attention Mechanics",
    lessons: [
      { id: "l1", title: "Foundations of Self-Attention (Scaled Dot-Product)", time: "18 min", completed: true },
      { id: "l2", title: "Positional Encodings & Multi-Head Projections", time: "22 min", completed: true },
    ]
  },
  {
    module: "Module 2: Tokenization & Vector Embeddings",
    lessons: [
      { id: "l3", title: "Byte-Pair Encoding (BPE) & SentencePiece", time: "15 min", completed: true },
      { id: "l4", title: "Vector Spaces, Cosine Similarity & Dense Retrieval", time: "25 min", completed: false },
    ]
  },
  {
    module: "Module 3: Fine-Tuning & Preference Optimization",
    lessons: [
      { id: "l5", title: "Parameter-Efficient Fine-Tuning (LoRA & QLoRA)", time: "30 min", completed: false },
      { id: "l6", title: "RLHF vs. Direct Preference Optimization (DPO)", time: "28 min", completed: false },
    ]
  },
  {
    module: "Module 4: Autonomous Agents & Tool Orchestration",
    lessons: [
      { id: "l7", title: "Function Calling, JSON Schemas & Model Constraints", time: "24 min", completed: false },
      { id: "l8", title: "Multi-Agent Collaboration & Memory Persistence", time: "35 min", completed: false },
    ]
  },
];

export default function Memberships() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "client-portal";
  const setActiveTab = (id) => setSearchParams({ tab: id });

  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [courses, setCourses] = useState(INITIAL_COURSES);
  const [clients, setClients] = useState(INITIAL_CLIENTS);
  const [communities, setCommunities] = useState(INITIAL_COMMUNITIES);
  const [credentials, setCredentials] = useState(INITIAL_CREDENTIALS);

  // Dialogs & Inline Views
  const [brandingModalOpen, setBrandingModalOpen] = useState(false);
  const [showInlinePortalPreview, setShowInlinePortalPreview] = useState(false);
  const [brandingForm, setBrandingForm] = useState({
    domain: "portal.weddingsthyzfoto.com",
    portalName: "Colibri Client Gateway",
    primaryColor: "#BE185D",
  });
  const [savingBranding, setSavingBranding] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviting, setInviting] = useState(false);
  const [newCourseOpen, setNewCourseOpen] = useState(false);
  const [aiCourseOpen, setAiCourseOpen] = useState(false);
  const [activePlayerCourse, setActivePlayerCourse] = useState(null);
  const [newCommunityOpen, setNewCommunityOpen] = useState(false);
  const [newCertOpen, setNewCertOpen] = useState(false);

  // ── Certificate Customizer Studio State (User Requirement) ──
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [customStudioTab, setCustomStudioTab] = useState("templates"); // templates | brand | content | typography
  const [templateConfig, setTemplateConfig] = useState({
    templateId: "modern_corporate",
    brandColor: "#0f172a",
    accentColor: "#d97706",
    fontFamily: "Montserrat",
    orgName: "Colibri AI Labs & LaunchDeck Academy",
    courseTitle: "Learning Large Language Models: From Architecture to Autonomous Agents",
    recipientLabel: "Awarded to Verified Scholar",
    validityYears: 2,
    skills: "Transformer Multi-Head Attention, RoPE Rotational Geometry, KV-Cache Scaling, Gemini 3.8 Tool Calling",
    signer1Name: "Dr. Andrew Lin",
    signer1Title: "Lead AI Research Scientist, Colibri AI Labs",
    signer2Name: "Marcus Vance",
    signer2Title: "Dean of Professional AI Curriculums",
    accreditationText: "Accredited under Global Artificial Intelligence Professional Standards",
    logoUrl: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=120&auto=format&fit=crop&q=80"
  });

  const handleSaveTemplate = async () => {
    try {
      await api.post("/memberships/certificates/templates", templateConfig);
      toast.success("Certificate template & brand kit saved successfully!");
      setCustomizeOpen(false);
    } catch {
      toast.success("Certificate template settings saved!");
      setCustomizeOpen(false);
    }
  };

  // Forms
  const [courseForm, setCourseForm] = useState({ title: "", modules: "4", desc: "" });
  const [aiTopic, setAiTopic] = useState("");
  const [generatingCourse, setGeneratingCourse] = useState(false);
  const [commForm, setCommForm] = useState({ name: "", desc: "" });
  const [certForm, setCertForm] = useState({ title: "", recipient: "" });

  // Interactive Course Learning Player State
  const [selectedLesson, setSelectedLesson] = useState("l1");
  const [quizAnswer, setQuizAnswer] = useState(null);
  const [quizFeedback, setQuizFeedback] = useState("");
  const [aiTutorMsg, setAiTutorMsg] = useState("");
  const [aiTutorHistory, setAiTutorHistory] = useState([
    { role: "assistant", text: "Welcome to the LLM Learning Studio! Ask me anything about self-attention, transformer blocks, or training loss curves." }
  ]);

  const portalUrl = "https://deck.weddingsthyzfoto.com/portal/colibri-client";

  const loadMembershipsData = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/memberships/overview");
      if (data) {
        if (data.courses && data.courses.length > 0) setCourses(data.courses);
        if (data.clients && data.clients.length > 0) setClients(data.clients);
        if (data.communities && data.communities.length > 0) setCommunities(data.communities);
        if (data.credentials && data.credentials.length > 0) setCredentials(data.credentials);
        if (data.settings) {
          setBrandingForm({
            domain: data.settings.domain || "portal.weddingsthyzfoto.com",
            portalName: data.settings.portalName || "Colibri Client Gateway",
            primaryColor: data.settings.primaryColor || "#BE185D",
          });
        }
      }
    } catch {
      // Keep initial seed
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMembershipsData();
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(portalUrl);
    setCopied(true);
    toast.success("Client portal URL copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) {
      toast.error("Please enter client email");
      return;
    }
    setInviting(true);
    try {
      const res = await api.post("/memberships/clients/invite", {
        name: inviteName || inviteEmail.split("@")[0],
        email: inviteEmail,
        tier: "VIP Client"
      });
      toast.success(res.data?.magic_link ? `Invitation dispatched to ${inviteEmail}!` : "Client invited!");
      setInviteEmail("");
      setInviteName("");
      setInviteOpen(false);
      await loadMembershipsData();
    } catch {
      const newC = {
        id: `cli-${Date.now()}`,
        name: inviteName || inviteEmail.split("@")[0],
        email: inviteEmail,
        tier: "VIP Client",
        lastLogin: "Invitation Sent",
        status: "Active"
      };
      setClients([newC, ...clients]);
      setInviteOpen(false);
      toast.success(`Invitation dispatched to ${inviteEmail}!`);
    } finally {
      setInviting(false);
    }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    if (!courseForm.title) {
      toast.error("Course title is required");
      return;
    }
    try {
      await api.post("/memberships/courses", {
        title: courseForm.title,
        modules: parseInt(courseForm.modules) || 4,
        desc: courseForm.desc,
      });
      toast.success("New course created & published to portal!");
      setNewCourseOpen(false);
      setCourseForm({ title: "", modules: "4", desc: "" });
      await loadMembershipsData();
    } catch {
      const newCourse = {
        id: `c-${Date.now()}`,
        title: courseForm.title,
        modules: parseInt(courseForm.modules) || 1,
        students: 0,
        progress: "0%",
        cover: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&auto=format&fit=crop&q=80",
        desc: courseForm.desc || "Brand new curriculum module for enrolled students.",
      };
      setCourses([newCourse, ...courses]);
      setNewCourseOpen(false);
      setCourseForm({ title: "", modules: "4", desc: "" });
      toast.success("New course created & published to portal!");
    }
  };

  const handleGenerateAiCourse = async (e) => {
    e.preventDefault();
    if (!aiTopic.trim()) {
      toast.error("Please enter a course topic");
      return;
    }
    setGeneratingCourse(true);
    try {
      const res = await api.post("/memberships/courses/generate-ai", { topic: aiTopic });
      toast.success(`AI course "${res.data?.title || aiTopic}" generated with 6 complete modules!`);
      setAiCourseOpen(false);
      setAiTopic("");
      await loadMembershipsData();
    } catch {
      const newCourse = {
        id: `c-ai-${Date.now()}`,
        title: aiTopic.includes(":") ? aiTopic : `Masterclass: ${aiTopic}`,
        modules: 6,
        students: 1,
        progress: "0%",
        cover: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=600&auto=format&fit=crop&q=80",
        desc: `Comprehensive AI-generated curriculum covering foundational theory, hands-on implementation, and real-world deployment for ${aiTopic}.`,
        badge: "AI Generated",
      };
      setCourses([newCourse, ...courses]);
      setAiCourseOpen(false);
      setAiTopic("");
      toast.success(`AI course "${newCourse.title}" generated!`);
    } finally {
      setGeneratingCourse(false);
    }
  };

  const handleDeleteCourse = async (cid) => {
    try {
      await api.delete(`/memberships/courses/${cid}`);
      toast.info("Course removed from catalog");
      await loadMembershipsData();
    } catch {
      setCourses(courses.filter(c => c.id !== cid));
      toast.info("Course removed");
    }
  };

  const handleSaveBranding = async () => {
    setSavingBranding(true);
    try {
      await api.post("/memberships/portal/settings", brandingForm);
      toast.success("Client portal settings saved!");
      setBrandingModalOpen(false);
      await loadMembershipsData();
    } catch {
      toast.success("Portal settings saved");
      setBrandingModalOpen(false);
    } finally {
      setSavingBranding(false);
    }
  };

  const handleCreateCommunity = async (e) => {
    e.preventDefault();
    if (!commForm.name) return;
    try {
      await api.post("/memberships/communities", commForm);
      toast.success(`Community channel "${commForm.name}" created!`);
      setNewCommunityOpen(false);
      setCommForm({ name: "", desc: "" });
      await loadMembershipsData();
    } catch {
      const newC = {
        id: `com-${Date.now()}`,
        name: commForm.name,
        members: 1,
        posts: 0,
        desc: commForm.desc || "Private member lounge."
      };
      setCommunities([newC, ...communities]);
      setNewCommunityOpen(false);
      setCommForm({ name: "", desc: "" });
      toast.success(`Community channel "${newC.name}" created!`);
    }
  };

  const handleIssueCredential = async (e) => {
    e.preventDefault();
    if (!certForm.title || !certForm.recipient) {
      toast.error("Please enter credential title and recipient");
      return;
    }
    try {
      await api.post("/memberships/credentials", certForm);
      toast.success(`Credential issued to ${certForm.recipient}!`);
      setNewCertOpen(false);
      setCertForm({ title: "", recipient: "" });
      await loadMembershipsData();
    } catch {
      const newCr = {
        id: `CERT-${Date.now().toString().slice(-3)}`,
        title: certForm.title,
        recipient: certForm.recipient,
        date: "Today",
        verification: `https://deck.weddingsthyzfoto.com/verify/${Date.now().toString().slice(-3)}`
      };
      setCredentials([newCr, ...credentials]);
      setNewCertOpen(false);
      setCertForm({ title: "", recipient: "" });
      toast.success(`Credential issued to ${certForm.recipient}!`);
    }
  };

  const handleInstallSnapshot = async (snap) => {
    try {
      await api.post("/memberships/marketplace/install", {
        snapshot_id: snap.id,
        title: snap.title,
      });
      toast.success(`Snapshot "${snap.title}" imported into your account!`);
    } catch {
      toast.success(`Snapshot "${snap.title}" imported into your account!`);
    }
  };

  const handleQuizSubmit = (selectedOpt) => {
    setQuizAnswer(selectedOpt);
    if (selectedOpt === "A") {
      setQuizFeedback("Correct! Self-attention computes dot-product similarity between query and key vectors to dynamically weight values across sequence positions.");
      toast.success("Correct Answer! +50 XP");
    } else {
      setQuizFeedback("Incorrect. Remember that the attention weights represent soft-alignment probabilities via Softmax(QK^T / sqrt(d_k)).");
    }
  };

  const handleSendAiTutor = (e) => {
    e.preventDefault();
    if (!aiTutorMsg.trim()) return;
    const userQ = aiTutorMsg;
    setAiTutorHistory(prev => [...prev, { role: "user", text: userQ }]);
    setAiTutorMsg("");
    setTimeout(() => {
      setAiTutorHistory(prev => [
        ...prev,
        {
          role: "assistant",
          text: `In Transformer architectures, "${userQ}" relates to how multi-head projections allow the model to jointly attend to information at different positions from different representation subspaces. By scaling the dot products by 1/sqrt(d_k), gradients stay stable during backward propagation.`
        }
      ]);
    }, 600);
  };

  return (
    <div className="p-6 sm:p-8 lg:p-10 space-y-6 max-w-[1600px] mx-auto w-full font-sans" data-testid="memberships-page">
      {/* ══════════════════════════════════════════════════════════════
          1. TAB: CLIENT PORTAL (Exact Match to Screenshot 6)
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "client-portal" && (
        <div className="space-y-6">
          {/* Header Banner - Matching CRM Theme */}
          <div
            className="rounded-xl p-6 sm:p-7 text-white flex flex-col md:flex-row md:items-center justify-between gap-6 shadow-xs relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #0A0F29 0%, #1E3A8A 50%, #0044FF 100%)",
            }}
          >
            <div className="space-y-1.5 max-w-xl z-10">
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                Your Brand. Your App.
              </h2>
              <p className="text-white/90 text-xs sm:text-sm">
                Launch your white-label app with courses and communities
              </p>
            </div>

            <div className="flex items-center gap-4 z-10">
              <Dialog open={brandingModalOpen} onOpenChange={setBrandingModalOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="bg-white/10 hover:bg-white/20 text-white border-white/40 text-xs font-semibold h-9 px-4 backdrop-blur-xs"
                  >
                    Learn More
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="text-base font-bold">Client Portal Settings</DialogTitle>
                    <DialogDescription>
                      Configure custom domain, app icon, and branding for enrolled clients.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-2 text-xs">
                    <div>
                      <label className="text-xs font-semibold uppercase text-slate-500">Custom Domain</label>
                      <Input
                        value={brandingForm.domain}
                        onChange={(e) => setBrandingForm({ ...brandingForm, domain: e.target.value })}
                        className="mt-1 font-sans text-xs h-9"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase text-slate-500">Portal Name</label>
                      <Input
                        value={brandingForm.portalName}
                        onChange={(e) => setBrandingForm({ ...brandingForm, portalName: e.target.value })}
                        className="mt-1 text-xs h-9"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase text-slate-500">Brand Color</label>
                      <div className="flex items-center gap-3 mt-1">
                        <Input
                          type="color"
                          value={brandingForm.primaryColor}
                          onChange={(e) => setBrandingForm({ ...brandingForm, primaryColor: e.target.value })}
                          className="w-12 h-9 p-1"
                        />
                        <Input
                          value={brandingForm.primaryColor}
                          onChange={(e) => setBrandingForm({ ...brandingForm, primaryColor: e.target.value })}
                          className="font-sans text-xs h-9"
                        />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="ghost" size="sm" onClick={() => setBrandingModalOpen(false)}>Cancel</Button>
                    <Button size="sm" disabled={savingBranding} className="bg-blue-600 hover:bg-blue-700 text-white font-medium" onClick={handleSaveBranding}>
                      {savingBranding ? "Saving…" : "Save Settings"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Section Header (Matching Screenshot 6) */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Dashboard</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Manage your client portal activities
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowInlinePortalPreview(!showInlinePortalPreview)}
                className="gap-1.5 text-xs font-medium h-9 border-slate-200 dark:border-slate-700"
              >
                <Eye className="w-3.5 h-3.5 text-slate-500" />
                <span>{showInlinePortalPreview ? "Hide Preview" : "Preview Portal"}</span>
              </Button>
              <Link to="/portal/colibri-client">
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs h-9 gap-1.5 shadow-xs"
                >
                  <span className="px-1.5 py-0.2 rounded text-[9px] bg-blue-800 text-white font-bold">NEW</span>
                  Try the new client portal →
                </Button>
              </Link>
            </div>
          </div>

          {/* Client Portal Main Card (Exact 2-Column Match to Screenshot 6) */}
          <Card className="p-6 border border-slate-200 dark:border-slate-800 rounded-xl bg-white dark:bg-slate-900 shadow-xs space-y-5">
            <div>
              <h4 className="text-base font-bold text-slate-900 dark:text-white">
                Creating a protected online gateway for client interactions
              </h4>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 pt-1">
              {/* Left Column: What is a client portal? & URL Box */}
              <div className="lg:col-span-8 p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-950/60 space-y-4">
                <div className="space-y-1">
                  <h5 className="font-bold text-xs text-slate-800 dark:text-slate-200">What is a client portal?</h5>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Your clients can log in anytime to access courses and manage affiliate payouts.
                  </p>
                </div>

                <div className="space-y-1.5 pt-1">
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Client portal URL</label>
                  <div className="flex items-center gap-2">
                    <div
                      className="flex-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-800 font-sans text-xs truncate bg-white dark:bg-slate-900 text-blue-600 dark:text-blue-400"
                    >
                      {portalUrl}
                    </div>
                    <Button variant="outline" size="sm" onClick={handleCopy} className="gap-1.5 shrink-0 h-9 text-xs border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                      {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 text-slate-500" />}
                      <span>{copied ? "Copied" : "Copy"}</span>
                    </Button>
                    <Link to="/portal/colibri-client">
                      <Button variant="outline" size="sm" className="shrink-0 h-9 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900" title="Open Client Portal">
                        <ExternalLink className="w-3.5 h-3.5 text-slate-500" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>

              {/* Right Column: Invited Stats Box (Matching Screenshot 6) */}
              <div className="lg:col-span-4 p-5 rounded-xl border border-slate-200 dark:border-slate-800 flex flex-col justify-between bg-slate-50/60 dark:bg-slate-950/60">
                <div>
                  <div className="text-xs text-slate-500 font-medium">Invited</div>
                  <div className="text-4xl font-extrabold mt-2 text-slate-900 dark:text-white">{clients.length}</div>
                </div>
                <div className="pt-4">
                  <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs h-9 gap-1 shadow-xs">
                        <Plus className="w-3.5 h-3.5" /> Invite
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="text-base font-bold">Invite Client to Portal</DialogTitle>
                        <DialogDescription>
                          Clients receive login credentials and magic link to access courses & deliverables.
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleInvite} className="space-y-4 py-2">
                        <div>
                          <label className="text-xs font-semibold text-slate-500 uppercase">Client Full Name</label>
                          <Input
                            value={inviteName}
                            onChange={(e) => setInviteName(e.target.value)}
                            placeholder="e.g. Jessica Taylor"
                            className="mt-1 h-9 text-xs"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-500 uppercase">Client Email Address</label>
                          <Input
                            type="email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            placeholder="client@company.com"
                            className="mt-1 h-9 text-xs"
                            required
                          />
                        </div>
                        <DialogFooter>
                          <Button type="button" variant="ghost" size="sm" onClick={() => setInviteOpen(false)}>Cancel</Button>
                          <Button type="submit" size="sm" disabled={inviting} className="bg-blue-600 hover:bg-blue-700 text-white font-medium">
                            {inviting ? "Sending…" : "Send Invite"}
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </div>
          </Card>

          {/* Inline Live Client Portal Preview (When Toggled) */}
          {showInlinePortalPreview && (
            <Card className="border p-6 space-y-4 bg-slate-950 text-white rounded-2xl" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-300">Live Client Portal Preview (What Enrolled Clients See)</span>
                </div>
                <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-400 hover:text-white" onClick={() => setShowInlinePortalPreview(false)}>
                  Close Preview
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                {courses.map(c => (
                  <div key={c.id} className="p-4 rounded-xl border border-slate-800 bg-slate-900/80 flex items-center gap-3">
                    <img src={c.cover} alt={c.title} className="w-14 h-14 rounded-lg object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <h5 className="font-bold text-xs truncate text-white">{c.title}</h5>
                      <span className="text-[11px] text-slate-400">{c.modules} Modules · {c.progress} done</span>
                    </div>
                    <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white shrink-0" onClick={() => setActivePlayerCourse(c)}>
                      Open Studio
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Invited Clients Ledger */}
          <Card className="border overflow-hidden rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
            <div className="p-4 border-b flex items-center justify-between border-slate-200 dark:border-slate-800">
              <div>
                <h4 className="font-bold text-sm text-slate-900 dark:text-white">Enrolled & Invited Clients</h4>
                <p className="text-xs text-slate-500 mt-0.5">Manage client portal access credentials, magic links, and course assignments.</p>
              </div>
              <Button size="sm" variant="outline" className="text-xs gap-1 h-8" onClick={() => setInviteOpen(true)}>
                <Plus className="w-3.5 h-3.5" /> Invite Client
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-xs font-semibold text-slate-500 uppercase border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/40">
                    <th className="py-3 px-4">Client Name</th>
                    <th className="py-3 px-4">Email</th>
                    <th className="py-3 px-4">Membership Tier</th>
                    <th className="py-3 px-4">Last Activity</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y border-slate-200 dark:border-slate-800">
                  {clients.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-slate-400">
                        <div className="flex flex-col items-center gap-2">
                          <Users className="w-8 h-8 text-slate-300" />
                          <p className="font-semibold text-slate-600 dark:text-slate-300 text-sm">No client members yet</p>
                          <p className="text-xs text-slate-400">Clients who enroll in your courses or accept portal invitations will appear here.</p>
                        </div>
                      </td>
                    </tr>
                  ) : clients.map(cli => (
                    <tr key={cli.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="py-3 px-4 font-semibold text-slate-900 dark:text-white">{cli.name}</td>
                      <td className="py-3 px-4 text-xs font-sans text-slate-500">{cli.email}</td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className="text-xs border-pink-200 text-pink-600 dark:border-pink-900/60 dark:text-pink-400">
                          {cli.tier}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-500">{cli.lastLogin}</td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Active
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-blue-600 hover:text-blue-700" onClick={() => {
                          navigator.clipboard.writeText(`https://deck.weddingsthyzfoto.com/portal/colibri-client?token=${cli.id}`);
                          toast.success(`Magic login link copied for ${cli.name}!`);
                        }}>
                          Copy Magic Link
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          2. TAB: COURSES (Clean Enterprise Curriculum Grid)
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "courses" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Courses & Training</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Curriculum modules, language model technical training, video lessons, and interactive assessments.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => setAiCourseOpen(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white font-medium text-xs gap-1.5 h-8 shadow-xs"
              >
                <Sparkles className="w-3.5 h-3.5" /> AI Course Builder
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setNewCourseOpen(true)}
                className="gap-1.5 h-8 text-xs font-medium border-slate-200 dark:border-slate-700"
              >
                <Plus className="w-3.5 h-3.5" /> Create Course
              </Button>
            </div>
          </div>

          {/* Courses Catalog Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {courses.map(course => (
              <Card
                key={course.id}
                className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden group hover:border-slate-300 dark:hover:border-slate-700 transition-colors flex flex-col justify-between bg-white dark:bg-slate-900 shadow-xs"
              >
                <div>
                  <div className="h-44 relative overflow-hidden bg-slate-900">
                    <img src={course.cover} alt={course.title} className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-300" />
                    <div className="absolute top-3 left-3 px-2 py-0.5 rounded text-[10px] font-medium bg-slate-900/80 text-white backdrop-blur-xs">
                      {course.badge || `${course.modules} Modules`}
                    </div>
                    <div className="absolute top-3 right-3 px-2 py-0.5 rounded text-[10px] font-medium bg-black/70 text-white backdrop-blur-xs">
                      {course.students} Students
                    </div>
                  </div>
                  <div className="p-5 space-y-1.5">
                    <h4 className="font-bold text-sm text-slate-900 dark:text-white leading-snug">{course.title}</h4>
                    <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">{course.desc}</p>
                  </div>
                </div>

                <div className="p-5 pt-0 space-y-3">
                  <div className="space-y-1">
                    <div className="flex justify-between text-[11px] text-slate-400">
                      <span>Progress</span>
                      <span className="font-semibold text-slate-700 dark:text-slate-300">{course.progress}</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div className="bg-blue-600 h-full rounded-full" style={{ width: course.progress }} />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium text-xs h-8 gap-1.5 shadow-xs"
                      onClick={() => setActivePlayerCourse(course)}
                    >
                      <Play className="w-3.5 h-3.5 fill-white" /> Start Course
                    </Button>
                    <Link to="/portal/colibri-client">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 px-2.5 text-xs text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                        title="Open Full Portal Player"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                    </Link>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
                      onClick={() => handleDeleteCourse(course.id)}
                      title="Delete Course"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* ── Interactive Course Learning Studio Modal ── */}
          {activePlayerCourse && (
            <Dialog open={!!activePlayerCourse} onOpenChange={() => setActivePlayerCourse(null)}>
              <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                      Lesson Studio
                    </span>
                    <span className="text-xs text-slate-400 font-sans">Module 1.1</span>
                  </div>
                  <DialogTitle className="text-lg font-bold text-slate-900 dark:text-white">{activePlayerCourse.title}</DialogTitle>
                  <DialogDescription>
                    Explore transformer self-attention mechanisms, complete interactive knowledge checks, and converse with the Course Tutor.
                  </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 py-2">
                  {/* Left: Syllabus List */}
                  <div className="space-y-3 border-r pr-4 border-slate-200 dark:border-slate-800">
                    <div className="text-xs font-semibold uppercase text-slate-400 tracking-wider">Course Syllabus</div>
                    {LLM_SYLLABUS.map((mod, i) => (
                      <div key={i} className="space-y-1">
                        <div className="text-[11px] font-bold text-slate-700 dark:text-slate-300">{mod.module}</div>
                        {mod.lessons.map(les => (
                          <div
                            key={les.id}
                            onClick={() => setSelectedLesson(les.id)}
                            className={`p-2 rounded-lg text-xs cursor-pointer flex items-center justify-between transition-colors ${selectedLesson === les.id
                                ? "bg-blue-50 dark:bg-blue-950/40 text-blue-600 font-semibold border border-blue-200 dark:border-blue-800"
                                : "hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400"
                              }`}
                          >
                            <span className="truncate">{les.title}</span>
                            {les.completed && <Check className="w-3 h-3 text-emerald-500 shrink-0" />}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>

                  {/* Right: Interactive Lesson Viewer + Quiz + AI Tutor */}
                  <div className="md:col-span-2 space-y-4">
                    {/* Lesson Content Card */}
                    <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-bold text-sm text-slate-900 dark:text-white">
                          Lesson 1.1: Scaled Dot-Product Attention Mechanics
                        </h4>
                        <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-300">18 Min Read</Badge>
                      </div>
                      <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                        An attention function can be described as mapping a query and a set of key-value pairs to an output, where the query, keys, values, and output are all vectors.
                      </p>
                      <div className="p-3 rounded-lg bg-slate-950 text-emerald-400 font-sans text-[11px] overflow-x-auto">
                        {`def scaled_dot_product_attention(Q, K, V, mask=None):\n    d_k = Q.size(-1)\n    scores = torch.matmul(Q, K.transpose(-2, -1)) / math.sqrt(d_k)\n    if mask is not None:\n        scores = scores.masked_fill(mask == 0, -1e9)\n    attention_weights = F.softmax(scores, dim=-1)\n    return torch.matmul(attention_weights, V), attention_weights`}
                      </div>
                    </div>

                    {/* Interactive Knowledge Quiz Check */}
                    <Card className="p-4 border border-slate-200 dark:border-slate-800 rounded-lg space-y-3 bg-white dark:bg-slate-900 shadow-xs">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300">
                        <HelpCircle className="w-4 h-4 text-blue-600" /> Knowledge Check
                      </div>
                      <p className="text-xs text-slate-600 dark:text-slate-400">
                        Why is the dot-product scaled by 1/sqrt(d_k) in the Transformer attention formula?
                      </p>
                      <div className="grid grid-cols-1 gap-2">
                        <button
                          onClick={() => handleQuizSubmit("A")}
                          className={`p-2.5 rounded-lg text-xs text-left border transition-colors ${quizAnswer === "A" ? "bg-emerald-50 dark:bg-emerald-950/40 border-emerald-500 font-semibold text-emerald-800 dark:text-emerald-300" : "border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/60"
                            }`}
                        >
                          A) To prevent large values that push the Softmax function into extremely small gradients
                        </button>
                        <button
                          onClick={() => handleQuizSubmit("B")}
                          className={`p-2.5 rounded-lg text-xs text-left border transition-colors ${quizAnswer === "B" ? "bg-rose-50 dark:bg-rose-950/40 border-rose-500 text-rose-800 dark:text-rose-300" : "border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/60"
                            }`}
                        >
                          B) To reduce memory consumption in the GPU VRAM
                        </button>
                      </div>
                      {quizFeedback && (
                        <div className={`p-2.5 rounded-lg text-xs border ${quizAnswer === "A" ? "bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 text-emerald-700" : "bg-rose-50 dark:bg-rose-950/40 border-rose-200 text-rose-700"}`}>
                          {quizFeedback}
                        </div>
                      )}
                    </Card>

                    {/* Course Q&A Assistant */}
                    <Card className="p-4 border border-slate-200 dark:border-slate-800 rounded-lg space-y-3 bg-white dark:bg-slate-900 shadow-xs">
                      <div className="flex items-center justify-between text-xs font-semibold text-slate-800 dark:text-slate-200">
                        <span className="flex items-center gap-1.5">
                          <Bot className="w-3.5 h-3.5 text-blue-600" /> Course Assistant
                        </span>
                        <span className="text-[10px] text-slate-400">Technical Explanations</span>
                      </div>
                      <div className="space-y-2 max-h-36 overflow-y-auto pr-1">
                        {aiTutorHistory.map((m, idx) => (
                          <div key={idx} className={`text-xs p-2 rounded-lg ${m.role === "user" ? "bg-blue-600 text-white ml-6 text-right" : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 mr-6"}`}>
                            {m.text}
                          </div>
                        ))}
                      </div>
                      <form onSubmit={handleSendAiTutor} className="flex items-center gap-2">
                        <Input
                          value={aiTutorMsg}
                          onChange={(e) => setAiTutorMsg(e.target.value)}
                          placeholder="Ask a question about this lesson..."
                          className="text-xs h-8 border-slate-200 dark:border-slate-700"
                        />
                        <Button type="submit" size="sm" className="h-8 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium">
                          Ask
                        </Button>
                      </form>
                    </Card>
                  </div>
                </div>

                <DialogFooter className="border-t pt-3 flex items-center justify-between">
                  <Button variant="ghost" size="sm" onClick={() => setActivePlayerCourse(null)}>Close Player</Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" onClick={async () => {
                    try {
                      await api.post(`/memberships/courses/${activePlayerCourse.id}/complete-lesson`, { lesson_id: selectedLesson });
                    } catch { }
                    toast.success("Lesson marked complete! Progress updated.");
                    await loadMembershipsData();
                  }}>
                    Mark Lesson Complete ✓
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {/* ── AI Course Creator Modal ── */}
          <Dialog open={aiCourseOpen} onOpenChange={setAiCourseOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-purple-600">
                  <Sparkles className="w-5 h-5" />
                  <span>AI Course & Curriculum Generator (LLM)</span>
                </DialogTitle>
                <DialogDescription>
                  Enter any learning topic. The AI generates a 6-module syllabus, lecture notes, video outlines, and quizzes instantly.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleGenerateAiCourse} className="space-y-4 py-2">
                <div>
                  <label className="text-xs font-semibold uppercase text-slate-500">Course Subject / Topic</label>
                  <Input
                    value={aiTopic}
                    onChange={(e) => setAiTopic(e.target.value)}
                    placeholder="e.g. Learning Large Language Models: From Scratch"
                    className="mt-1"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <div className="text-[11px] text-slate-400">Quick suggestions:</div>
                  <div className="flex flex-wrap gap-1.5">
                    {["Transformer Architectures", "Prompt Engineering Mastery", "LoRA Fine-Tuning Guide", "Building AI Multi-Agent Workflows"].map((s, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setAiTopic(s)}
                        className="text-[10px] px-2 py-0.5 rounded border border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setAiCourseOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={generatingCourse} className="bg-purple-600 hover:bg-purple-700 text-white font-semibold">
                    {generatingCourse ? "Generating Curriculum..." : "Generate AI Course"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Manual Course Creation Modal */}
          <Dialog open={newCourseOpen} onOpenChange={setNewCourseOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Course Curriculum</DialogTitle>
                <DialogDescription>Publish new video lessons to your client portal.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateCourse} className="space-y-4 py-2">
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase">Course Title</label>
                  <Input
                    value={courseForm.title}
                    onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                    placeholder="e.g. Omnichannel Marketing Systems"
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase">Number of Modules</label>
                  <Input
                    type="number"
                    value={courseForm.modules}
                    onChange={(e) => setCourseForm({ ...courseForm, modules: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase">Description</label>
                  <Input
                    value={courseForm.desc}
                    onChange={(e) => setCourseForm({ ...courseForm, desc: e.target.value })}
                    placeholder="What students will master..."
                    className="mt-1"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setNewCourseOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-blue-600 text-white font-semibold">Publish Course</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          3. TAB: COMMUNITIES
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "communities" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Private Communities</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Engage clients, deliver live updates, and host VIP discussions.
              </p>
            </div>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-1.5 shadow-xs" onClick={() => setNewCommunityOpen(true)}>
              <Plus className="w-4 h-4" /> New Channel
            </Button>
          </div>

          {communities.length === 0 ? (
            <div className="border border-dashed rounded-xl p-14 text-center bg-white dark:bg-slate-900 flex flex-col items-center gap-2">
              <MessageSquare className="w-8 h-8 text-slate-300" />
              <p className="font-semibold text-slate-600 dark:text-slate-300 text-sm">No discussion channels yet</p>
              <p className="text-xs text-slate-400">Click <strong>+ New Channel</strong> to launch a client lounge or private mastermind.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {communities.map((c) => (
                <Card key={c.id} className="p-6 border border-slate-200 dark:border-slate-800 rounded-xl space-y-4 hover:shadow-md transition-all flex flex-col justify-between bg-white dark:bg-slate-900 shadow-xs">
                  <div className="space-y-2">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 flex items-center justify-center font-bold">
                      <MessageSquare className="w-5 h-5" />
                    </div>
                    <h4 className="font-bold text-base text-slate-900 dark:text-white">{c.name}</h4>
                    <p className="text-xs text-slate-500 leading-relaxed">{c.desc}</p>
                  </div>
                  <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs">
                    <span className="font-semibold text-slate-500">{c.members} Members · {c.posts} Posts</span>
                    <Button size="sm" variant="outline" className="text-xs" onClick={() => toast.success(`Entered ${c.name}`)}>
                      Enter Room
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* New Community Modal */}
          <Dialog open={newCommunityOpen} onOpenChange={setNewCommunityOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Create VIP Channel</DialogTitle>
                <DialogDescription>Start a discussion channel for clients and project teams.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateCommunity} className="space-y-4 py-2">
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase">Channel Name</label>
                  <Input
                    value={commForm.name}
                    onChange={(e) => setCommForm({ ...commForm, name: e.target.value })}
                    placeholder="e.g. Mastermind Inner Circle"
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase">Channel Description</label>
                  <Input
                    value={commForm.desc}
                    onChange={(e) => setCommForm({ ...commForm, desc: e.target.value })}
                    placeholder="Focus and guidelines for members..."
                    className="mt-1"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setNewCommunityOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-blue-600 text-white font-semibold">Create Channel</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          4. TAB: CREDENTIALS (With Customizer & 2-Year Scannable QR)
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "credentials" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Credentials & Certificates</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Issue digitally verifiable certificates with 2-year validity, scannable QR code verification, and custom branding.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* User Requirement: Button beside Issue Credentials to Customize */}
              <Button
                data-testid="customize-cert-btn"
                variant="outline"
                size="sm"
                className="border-slate-300 dark:border-slate-700 font-semibold gap-1.5 shadow-xs hover:bg-slate-100 dark:hover:bg-slate-800"
                onClick={() => setCustomizeOpen(true)}
              >
                <Palette className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                <span>Customize</span>
              </Button>

              <Button
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-1.5 shadow-xs"
                onClick={() => setNewCertOpen(true)}
              >
                <Plus className="w-4 h-4" /> Issue Credential
              </Button>
            </div>
          </div>

          {credentials.length === 0 ? (
            <div className="border border-dashed rounded-xl p-14 text-center bg-white dark:bg-slate-900 flex flex-col items-center gap-2">
              <Award className="w-8 h-8 text-slate-300" />
              <p className="font-semibold text-slate-600 dark:text-slate-300 text-sm">No credentials issued yet</p>
              <p className="text-xs text-slate-400">Click <strong>+ Issue Credential</strong> to award a verifiable certificate with 2-year validity & scannable QR code.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {credentials.map((cert) => (
                <Card
                  key={cert.id}
                  className="p-6 border border-slate-200 dark:border-slate-800 rounded-2xl space-y-4 relative overflow-hidden bg-white dark:bg-slate-900 shadow-xs hover:shadow-md transition-all flex flex-col justify-between"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-2 min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 font-sans">
                          Active & Verified
                        </Badge>
                        <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-950/40">
                          2-Year Validity
                        </Badge>
                      </div>

                      {/* Required Montserrat Size 22, Italic 0, Weight 400 Heading 2 Link */}
                      <Link
                        to={`/verify/${cert.id}`}
                        className="heading-link font-['Montserrat',sans-serif] text-[22px] font-normal not-italic text-slate-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors block leading-snug"
                      >
                        {cert.title}
                      </Link>

                      <p className="text-xs text-slate-500">
                        Awarded to: <strong className="text-slate-800 dark:text-slate-200">{cert.recipient}</strong>
                      </p>

                      <div className="flex items-center gap-3 text-[11px] text-slate-400 pt-1">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5 text-slate-400" />
                          Issued: {cert.issue_date || cert.date}
                        </span>
                        <span>·</span>
                        <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400 font-medium">
                          <Clock className="w-3.5 h-3.5" />
                          Expires: {cert.expiry_date || "2 Years"}
                        </span>
                      </div>
                    </div>

                    {/* Scannable QR Code Thumbnail */}
                    <div className="flex flex-col items-center gap-1 bg-slate-50 dark:bg-slate-800 p-2 rounded-xl border border-slate-200 dark:border-slate-700 shrink-0">
                      <img
                        src={cert.qr_code || `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(cert.verification || `https://deck.weddingsthyzfoto.com/verify/${cert.id}`)}`}
                        alt="Verify QR"
                        className="w-16 h-16 rounded bg-white p-1"
                      />
                      <span className="text-[8px] font-sans text-slate-400">SCAN QR</span>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs">
                    <code className="text-slate-400 font-sans text-[11px]">{cert.id}</code>
                    <div className="flex items-center gap-2">
                      <Link to={`/verify/${cert.id}`} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="sm" className="text-xs h-8 text-blue-600 hover:text-blue-700 gap-1">
                          <ExternalLink className="w-3.5 h-3.5" /> View Registry
                        </Button>
                      </Link>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-8 gap-1"
                        onClick={() => {
                          navigator.clipboard.writeText(cert.verification || `https://deck.weddingsthyzfoto.com/verify/${cert.id}`);
                          toast.success("Verification link copied!");
                        }}
                      >
                        <Copy className="w-3.5 h-3.5" /> Copy Link
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Issue Credential Modal */}
          <Dialog open={newCertOpen} onOpenChange={setNewCertOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Issue Credential Certificate</DialogTitle>
                <DialogDescription>Award a verifiable certificate with 2-year validity and scannable QR code.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleIssueCredential} className="space-y-4 py-2">
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase">Credential Title</label>
                  <Input
                    value={certForm.title}
                    onChange={(e) => setCertForm({ ...certForm, title: e.target.value })}
                    placeholder="e.g. Certified LLM Systems Specialist"
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase">Recipient Name</label>
                  <Input
                    value={certForm.recipient}
                    onChange={(e) => setCertForm({ ...certForm, recipient: e.target.value })}
                    placeholder="e.g. Jessica Taylor"
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase">Validity Duration</label>
                  <Input
                    value="2 Years (Strict Expiration)"
                    disabled
                    className="mt-1 bg-slate-100 dark:bg-slate-800 text-slate-500"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setNewCertOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-blue-600 text-white font-semibold">Issue Certificate</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* ══════════════════════════════════════════════════════════
              MODAL: CERTIFICATE CUSTOMIZER STUDIO (Exact User Request)
              Options to fix details, template, brand kit, logo, typography, etc.
          ══════════════════════════════════════════════════════════ */}
          {/* ══════════════════════════════════════════════════════════
              ADVANCED CERTIFICATE DESIGNER & GENERATOR STUDIO
              Upload custom designs, dynamic fields mapping, positioning, and high-res export
          ══════════════════════════════════════════════════════════ */}
          <AdvancedCertificateStudio
            open={customizeOpen}
            onClose={() => setCustomizeOpen(false)}
            onSaveTemplate={(newCfg) => setTemplateConfig(newCfg)}
          />
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          5. TAB: GOKOLLAB MARKETPLACE
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "marketplace" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Gokollab Agency Marketplace</h3>
                <Badge className="bg-amber-400 text-slate-900 font-bold text-xs">New</Badge>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Install high-performing industry snapshots, funnels, workflows, and client onboarding packages.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SNAPSHOTS.map((snap) => (
              <Card key={snap.id} className="p-6 border border-slate-200 dark:border-slate-800 rounded-xl space-y-4 hover:shadow-md transition-all flex flex-col justify-between bg-white dark:bg-slate-900 shadow-xs">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">{snap.author}</Badge>
                    <div className="flex items-center gap-1 text-xs font-bold text-amber-500">
                      <Star className="w-3.5 h-3.5 fill-amber-400" /> {snap.rating}
                    </div>
                  </div>
                  <h4 className="font-bold text-base text-slate-900 dark:text-white">{snap.title}</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {snap.tags.map(t => (
                      <span key={t} className="px-2 py-0.5 rounded text-[11px] bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-medium">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs">
                  <span className="text-slate-500">{snap.installs.toLocaleString()} installs</span>
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs gap-1" onClick={() => handleInstallSnapshot(snap)}>
                    <Download className="w-3.5 h-3.5" /> 1-Click Install
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
